// src/components/AGTFit/workout/logworkout/StartEmptyWorkout.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Container,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Fab,
  Paper,
  Chip,
  useTheme,
  TextField,
  Grid,
  MenuItem,
  Select,
  InputAdornment,
  useScrollTrigger
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Dumbbell,
  Plus,
  ChevronLeft,
  X,
  Play,
  Pause,
  Check,
  RotateCcw,
  Trophy,
  AlertTriangle
} from 'lucide-react';
import RoutineExerciseExplorer from '../explorer/RoutineExerciseExplorer';
import agtfitService from '../../service/agtfitService';

// Color principal
const FIT_ORANGE = '#FF6B00';
const APPLE_RED = '#FF3B30';
const GRADIENT = 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)';

// Colores para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)'
};

const WorkoutContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(4, 3, 3),
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? 'transparent'
    : (theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF'),
  maxWidth: '1200px',
  height: '100%',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3, 1, 2),
  }
}));

const TopBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: theme.spacing(4),
}));

const ExerciseCard = styled(Paper)(({ theme }) => ({
  borderRadius: 16,
  overflow: 'hidden',
  marginBottom: theme.spacing(2),
  border: theme.palette.actualMode === 'wallpaper'
    ? `1px solid ${WALLPAPER_COLORS.border}`
    : `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)'}`,
  boxShadow: 'none',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? theme.palette.background.paper : '#FFFFFF'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
}));

const ExerciseHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: theme.palette.actualMode === 'wallpaper'
    ? `1px solid ${WALLPAPER_COLORS.border}`
    : `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)'}`,
}));

const SetItem = styled(ListItem)(({ theme, isRest, setType }) => ({
  backgroundColor: isRest
    ? (theme.palette.actualMode === 'wallpaper'
      ? 'rgba(255, 107, 0, 0.2)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(255, 107, 0, 0.15)'
        : 'rgba(255, 107, 0, 0.05)'))
    : 'transparent',
  borderRadius: 8,
  margin: theme.spacing(0.5, 0),
  borderLeft: setType === 'W'
    ? '4px solid #FFB74D' // Amarillo para Warm Up
    : setType === 'F'
    ? '4px solid #F44336' // Rojo para Failure
    : setType === 'D'
    ? '4px solid #2196F3' // Azul para Drop Set
    : 'none',
}));

const SetTypeChip = styled(Box)(({ theme, setType }) => ({
  width: 24,
  height: 24,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 600,
  fontSize: '12px',
  color: '#FFFFFF',
  backgroundColor: 
    setType === 'W' ? '#FFB74D' : // Amarillo para Warm Up
    setType === 'F' ? '#F44336' : // Rojo para Failure
    setType === 'D' ? '#2196F3' : // Azul para Drop Set
    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
}));

const AddExerciseFab = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(4),
  right: theme.spacing(4),
  background: GRADIENT,
  color: '#FFFFFF',
  '&:hover': {
    background: 'linear-gradient(90deg, #FF8800 0%, #FF5500 100%)',
  },
  zIndex: 1000,
}));

// const WorkoutCompleteDialog = styled(Dialog)(({ theme }) => ({
//   '& .MuiDialog-paper': {
//     borderRadius: 16,
//     padding: theme.spacing(2),
//     maxWidth: 500,
//   }
// }));

// const StatCard = styled(Box)(({ theme }) => ({
//   padding: theme.spacing(2),
//   borderRadius: 12,
//   backgroundColor: theme.palette.mode === 'dark' ? '#1A1A1A' : '#F8F8F8',
//   display: 'flex',
//   flexDirection: 'column',
//   alignItems: 'center',
//   justifyContent: 'center',
// }));

// const SetColumn = styled(Box)(({ theme }) => ({
//   display: 'flex',
//   alignItems: 'center',
//   justifyContent: 'center',
//   padding: theme.spacing(0, 1),
// }));

const SetHeaderItem = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.75rem',
  color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.textSecondary : theme.palette.text.secondary,
  textTransform: 'uppercase',
  textAlign: 'center',
}));

// const GradientText = styled(Typography)(({ theme }) => ({
//   background: GRADIENT,
//   WebkitBackgroundClip: 'text',
//   WebkitTextFillColor: 'transparent',
//   fontWeight: 600,
// }));

// const GradientProgress = styled(LinearProgress)(({ theme }) => ({
//   height: 8,
//   borderRadius: 4,
//   backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
//   '& .MuiLinearProgress-bar': {
//     background: GRADIENT,
//   }
// }));

// const RPEOption = styled(Box)(({ theme, selected }) => ({
//   padding: theme.spacing(1.5),
//   borderRadius: 8,
//   textAlign: 'center',
//   cursor: 'pointer',
//   border: `1px solid ${selected ? FIT_ORANGE : theme.palette.divider}`,
//   backgroundColor: selected
//     ? theme.palette.mode === 'dark' ? 'rgba(255,107,0,0.15)' : 'rgba(255,107,0,0.05)'
//     : 'transparent',
//   transition: 'all 0.2s',
//   '&:hover': {
//     backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,107,0,0.1)' : 'rgba(255,107,0,0.03)',
//   }
// }));

// Componente principal
const StartEmptyWorkout = ({ onBack, selectedRoutine = null, onWorkoutComplete, todaysActivityRef }) => {
  const theme = useTheme();
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openExerciseExplorer, setOpenExerciseExplorer] = useState(false);
  const [completedSets, setCompletedSets] = useState({});
  const [activeRestTimer, setActiveRestTimer] = useState(null);
  const [restTime, setRestTime] = useState(60); // 60 segundos por defecto
  const [timeLeft, setTimeLeft] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [, setWorkoutStartTime] = useState(null);
  const [workoutTime, setWorkoutTime] = useState(0);
  const [workoutActive, setWorkoutActive] = useState(true);
  const [showSets, setShowSets] = useState(true);
  const [openWorkoutComplete, setOpenWorkoutComplete] = useState(false);
  const [workoutStats, setWorkoutStats] = useState({
    duration: 0,
    caloriesBurned: 0,
    exerciseCount: 0,
    musclesWorked: [],
    totalSets: 0,
    completedSets: 0,
    totalVolume: 0,
  });
  const [restTimers, setRestTimers] = useState({});
  const [totalRestTime, setTotalRestTime] = useState(0);
  
  // Estado para los sets
  const [setData, setSetData] = useState({});
  
  // Estado para modales
  const [openSetTypeModal, setOpenSetTypeModal] = useState(false);
  const [openWeightModal, setOpenWeightModal] = useState(false);
  const [openRPEModal, setOpenRPEModal] = useState(false);
  const [openDiscardModal, setOpenDiscardModal] = useState(false);
  const [currentExerciseId, setCurrentExerciseId] = useState(null);
  const [currentSetIndex, setCurrentSetIndex] = useState(null);
  const [weightUnit, setWeightUnit] = useState('kg');

  // Referencias para temporizadores
  const timerRef = useRef(null);
  const workoutTimerRef = useRef(null);

  // Función para iniciar el temporizador del entrenamiento
  const startWorkoutTimer = useCallback(() => {
    workoutTimerRef.current = setInterval(() => {
      setWorkoutTime(prevTime => prevTime + 1);
    }, 1000);
  }, []);

  // Efecto para cargar la rutina seleccionada si existe
  useEffect(() => {
    if (selectedRoutine && selectedRoutine.exercises) {
      // Transformar los ejercicios de la rutina al formato requerido
      const formattedExercises = selectedRoutine.exercises.map(exercise => ({
        ...exercise,
        id: exercise.id || exercise.exerciseId,
        exerciseId: exercise.id || exercise.exerciseId,
        sets: exercise.sets || 3,
        reps: exercise.reps || 10,
        weight: exercise.weight || 0,
        restTime: exercise.restTime || 60,
        caloriesPerSet: exercise.baseCalories || 5,
      }));
      
      setExercises(formattedExercises);
      
      // Inicializar el objeto de sets completados y datos de sets
      const initialCompletedSets = {};
      const initialSetData = {};
      
      formattedExercises.forEach(exercise => {
        initialCompletedSets[exercise.id] = Array(exercise.sets).fill(false);
        
        // Inicializar datos para cada set
        initialSetData[exercise.id] = Array(exercise.sets).fill(0).map(() => ({
          type: 'N', // Normal por defecto (W=Warm Up, F=Failure, D=Drop)
          weight: exercise.weight || 0,
          reps: exercise.reps || 10,
          rpe: 0,
          completed: false,
          previousWeight: exercise.weight || 0,
        }));
      });
      
      setCompletedSets(initialCompletedSets);
      setSetData(initialSetData);
    }
    
    // Iniciar el temporizador del entrenamiento
    setWorkoutStartTime(Date.now());
    startWorkoutTimer();
    
    // Limpiar temporizadores al desmontar
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (workoutTimerRef.current) clearInterval(workoutTimerRef.current);
    };
  }, [selectedRoutine, startWorkoutTimer]);

  // Añadir este efecto para manejar la conversión de unidades si es necesario
  useEffect(() => {
    // Si se cambia entre kg y lbs, podríamos convertir los valores
    // Este es un ejemplo simple, podrías querer implementar una lógica más sofisticada
    if (weightUnit === 'kg' || weightUnit === 'lbs') {
      // Por ahora solo actualizamos la interfaz para mostrar la nueva unidad
      // Si quisieras convertir los valores, aquí es donde lo harías
    }
  }, [weightUnit]);

  // Función para añadir ejercicios desde el explorador
  const handleAddExercises = (selectedExercises) => {
    // Transformar los ejercicios seleccionados al formato requerido
    const newExercises = selectedExercises.map(exercise => ({
      ...exercise,
      id: exercise.id || exercise.exerciseId,
      exerciseId: exercise.id || exercise.exerciseId,
      sets: exercise.sets || 3,
      reps: exercise.reps || 10,
      weight: exercise.weight || 0,
      restTime: exercise.restTime || 60,
      caloriesPerSet: exercise.baseCalories || 5,
    }));
    
    setExercises(prevExercises => [...prevExercises, ...newExercises]);
    
    // Actualizar el objeto de sets completados y datos de sets
    const updatedCompletedSets = { ...completedSets };
    const updatedSetData = { ...setData };
    
    newExercises.forEach(exercise => {
      updatedCompletedSets[exercise.id] = Array(exercise.sets).fill(false);
      
      // Inicializar datos para cada set
      updatedSetData[exercise.id] = Array(exercise.sets).fill(0).map(() => ({
        type: 'N', // Normal por defecto
        weight: exercise.weight || 0,
        reps: exercise.reps || 10,
        rpe: 0,
        completed: false,
        previousWeight: exercise.weight || 0,
      }));
    });
    
    setCompletedSets(updatedCompletedSets);
    setSetData(updatedSetData);
  };

  const handleDeleteExercise = (exerciseId) => {
    // Filtrar el ejercicio a eliminar
    const updatedExercises = exercises.filter(ex => ex.id !== exerciseId);
    setExercises(updatedExercises);
  
    // Actualizar los objetos relacionados
    const updatedCompletedSets = { ...completedSets };
    const updatedSetData = { ...setData };
  
    delete updatedCompletedSets[exerciseId];
    delete updatedSetData[exerciseId];
  
    setCompletedSets(updatedCompletedSets);
    setSetData(updatedSetData);
  };

  // Removed unused function: formatMuscle
  // const formatMuscle = (muscleName) => {
  //   if (!muscleName) return '';
  //   return muscleName.charAt(0).toUpperCase() + muscleName.slice(1).toLowerCase();
  // };

  // Función para manejar el check de un set
  const handleSetCheck = (exerciseId, setIndex) => {
    const updatedCompletedSets = { ...completedSets };
    const updatedSetData = { ...setData };
    
    // Actualizar el estado del set (toggle)
    const newCompletedState = !updatedCompletedSets[exerciseId][setIndex];
    updatedCompletedSets[exerciseId][setIndex] = newCompletedState;
    
    // Actualizar también en los datos del set
    if (updatedSetData[exerciseId] && updatedSetData[exerciseId][setIndex]) {
      updatedSetData[exerciseId][setIndex].completed = newCompletedState;
    }
    
    setCompletedSets(updatedCompletedSets);
    setSetData(updatedSetData);
    
    // Si el set se marca como completado y no es el último set, iniciar temporizador de descanso
    if (newCompletedState && setIndex < completedSets[exerciseId].length - 1) {
      // Encontrar el ejercicio actual para obtener su tiempo de descanso
      const currentExercise = exercises.find(ex => ex.id === exerciseId);
      const restTimeValue = currentExercise?.restTime || 60;
      
      // Iniciar temporizador de descanso para este ejercicio y set
      startRestTimer(exerciseId, setIndex, restTimeValue);
    }
  };

  // Función para añadir un set a un ejercicio
  const handleAddSet = (exerciseId) => {
    const updatedCompletedSets = { ...completedSets };
    const updatedSetData = { ...setData };
    
    // Añadir un nuevo set al ejercicio
    if (updatedCompletedSets[exerciseId]) {
      updatedCompletedSets[exerciseId].push(false);
    }
    
    // Añadir datos para el nuevo set
    if (updatedSetData[exerciseId]) {
      const previousSets = updatedSetData[exerciseId];
      const lastSet = previousSets.length > 0 ? previousSets[previousSets.length - 1] : null;
      
      updatedSetData[exerciseId].push({
        type: 'N', // Normal por defecto
        weight: lastSet ? lastSet.weight : 0,
        reps: lastSet ? lastSet.reps : 10,
        rpe: 0,
        completed: false,
        previousWeight: lastSet ? lastSet.weight : 0,
      });
    }
    
    setCompletedSets(updatedCompletedSets);
    setSetData(updatedSetData);
    
    // Actualizar el ejercicio para reflejar el nuevo número de sets
    setExercises(prevExercises => 
      prevExercises.map(ex => 
        ex.id === exerciseId 
          ? { ...ex, sets: (ex.sets || 0) + 1 } 
          : ex
      )
    );
  };

  // Función para eliminar un set
  const handleRemoveSet = (exerciseId, setIndex) => {
    const updatedCompletedSets = { ...completedSets };
    const updatedSetData = { ...setData };
    
    // Eliminar el set
    if (updatedCompletedSets[exerciseId] && updatedCompletedSets[exerciseId].length > 1) {
      updatedCompletedSets[exerciseId].splice(setIndex, 1);
      updatedSetData[exerciseId].splice(setIndex, 1);
      
      // Actualizar el ejercicio para reflejar el nuevo número de sets
      setExercises(prevExercises => 
        prevExercises.map(ex => 
          ex.id === exerciseId 
            ? { ...ex, sets: Math.max(1, (ex.sets || 0) - 1) } 
            : ex
        )
      );
      
      setCompletedSets(updatedCompletedSets);
      setSetData(updatedSetData);
    }
  };

  // Función para abrir el modal de tipo de set
  const handleOpenSetTypeModal = (exerciseId, setIndex) => {
    setCurrentExerciseId(exerciseId);
    setCurrentSetIndex(setIndex);
    setOpenSetTypeModal(true);
  };

  // Función para seleccionar el tipo de set
  const handleSelectSetType = (type) => {
    if (currentExerciseId && currentSetIndex !== null) {
      const updatedSetData = { ...setData };
      
      if (updatedSetData[currentExerciseId] && updatedSetData[currentExerciseId][currentSetIndex]) {
        updatedSetData[currentExerciseId][currentSetIndex].type = type;
        setSetData(updatedSetData);
      }
    }
    
    setOpenSetTypeModal(false);
  };

  // Función para abrir el modal de peso
  // const handleOpenWeightModal = (exerciseId, setIndex) => {
  //   setCurrentExerciseId(exerciseId);
  //   setCurrentSetIndex(setIndex);
  //   setOpenWeightModal(true);
  // };

  // Función para guardar el peso
  // const handleSaveWeight = (weight) => {
  //   if (currentExerciseId && currentSetIndex !== null) {
  //     const updatedSetData = { ...setData };
  //
  //     if (updatedSetData[currentExerciseId] && updatedSetData[currentExerciseId][currentSetIndex]) {
  //       const weightValue = parseFloat(weight) || 0;
  //       updatedSetData[currentExerciseId][currentSetIndex].weight = weightValue;
  //       setSetData(updatedSetData);
  //     }
  //   }
  //
  //   setOpenWeightModal(false);
  // };

  // Función para abrir el modal de RPE
  const handleOpenRPEModal = (exerciseId, setIndex) => {
    setCurrentExerciseId(exerciseId);
    setCurrentSetIndex(setIndex);
    setOpenRPEModal(true);
  };

  // Función para seleccionar RPE
  const handleSelectRPE = (rpeValue) => {
    if (currentExerciseId && currentSetIndex !== null) {
      const updatedSetData = { ...setData };
      
      if (updatedSetData[currentExerciseId] && updatedSetData[currentExerciseId][currentSetIndex]) {
        updatedSetData[currentExerciseId][currentSetIndex].rpe = rpeValue;
        setSetData(updatedSetData);
      }
    }
    
    setOpenRPEModal(false);
  };

  // Función para actualizar las repeticiones
  const handleUpdateReps = (exerciseId, setIndex, reps) => {
    const updatedSetData = { ...setData };
    
    if (updatedSetData[exerciseId] && updatedSetData[exerciseId][setIndex]) {
      updatedSetData[exerciseId][setIndex].reps = parseInt(reps) || 0;
      setSetData(updatedSetData);
    }
  };

  // Función para iniciar el temporizador de descanso
  const startRestTimer = (exerciseId, setIndex, duration) => {
    // Detener cualquier temporizador activo
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    // Configurar el nuevo temporizador
    setActiveRestTimer({ exerciseId, setIndex });
    setTimeLeft(duration);
    setIsTimerRunning(true);
    setRestTime(duration);
    
    // Actualizar los temporizadores de descanso
    const updatedRestTimers = { ...restTimers };
    updatedRestTimers[`${exerciseId}-${setIndex}`] = {
      timeLeft: duration,
      isRunning: true,
    };
    setRestTimers(updatedRestTimers);
    
    // Iniciar el intervalo
    timerRef.current = setInterval(() => {
      setTimeLeft(prevTime => {
        if (prevTime <= 1) {
          // Detener el temporizador cuando llega a cero
          clearInterval(timerRef.current);
          setIsTimerRunning(false);
          setActiveRestTimer(null);
          
          // Actualizar el tiempo total de descanso
          setTotalRestTime(prev => prev + duration);
          
          // Actualizar los temporizadores de descanso
          const updatedTimers = { ...restTimers };
          updatedTimers[`${exerciseId}-${setIndex}`] = {
            timeLeft: 0,
            isRunning: false,
          };
          setRestTimers(updatedTimers);
          
          return 0;
        }
        return prevTime - 1;
      });
      
      // Actualizar el temporizador específico
      setRestTimers(prevTimers => {
        const updatedTimers = { ...prevTimers };
        if (updatedTimers[`${exerciseId}-${setIndex}`]) {
          updatedTimers[`${exerciseId}-${setIndex}`].timeLeft -= 1;
        }
        return updatedTimers;
      });
    }, 1000);
  };

  // Función para ajustar el tiempo de descanso
  const handleAdjustRestTime = (newTime) => {
    const timeValue = Math.max(0, Math.min(300, parseInt(newTime) || 0)); // Limitar entre 0s y 5min
    setRestTime(timeValue);
    
    if (activeRestTimer) {
      // Si hay un temporizador activo, actualizar su duración
      setTimeLeft(timeValue);
    }
  };

  // Función para pausar/reanudar el temporizador de descanso
  const toggleRestTimer = () => {
    if (isTimerRunning) {
      // Pausar
      clearInterval(timerRef.current);
      setIsTimerRunning(false);
      
      // Actualizar el temporizador específico
      if (activeRestTimer) {
        const { exerciseId, setIndex } = activeRestTimer;
        setRestTimers(prevTimers => {
          const updatedTimers = { ...prevTimers };
          if (updatedTimers[`${exerciseId}-${setIndex}`]) {
            updatedTimers[`${exerciseId}-${setIndex}`].isRunning = false;
          }
          return updatedTimers;
        });
      }
    } else {
      // Reanudar
      if (activeRestTimer) {
        const { exerciseId, setIndex } = activeRestTimer;
        
        timerRef.current = setInterval(() => {
          setTimeLeft(prevTime => {
            if (prevTime <= 1) {
              clearInterval(timerRef.current);
              setIsTimerRunning(false);
              setActiveRestTimer(null);
              
              // Actualizar el tiempo total de descanso
              setTotalRestTime(prev => prev + (restTime - timeLeft));
              
              // Actualizar el temporizador específico
              setRestTimers(prevTimers => {
                const updatedTimers = { ...prevTimers };
                if (updatedTimers[`${exerciseId}-${setIndex}`]) {
                  updatedTimers[`${exerciseId}-${setIndex}`].isRunning = false;
                  updatedTimers[`${exerciseId}-${setIndex}`].timeLeft = 0;
                }
                return updatedTimers;
              });
              
              return 0;
            }
            return prevTime - 1;
          });
          
          // Actualizar el temporizador específico
          setRestTimers(prevTimers => {
            const updatedTimers = { ...prevTimers };
            if (updatedTimers[`${exerciseId}-${setIndex}`]) {
              updatedTimers[`${exerciseId}-${setIndex}`].timeLeft -= 1;
            }
            return updatedTimers;
          });
        }, 1000);
        
        setIsTimerRunning(true);
        
        // Actualizar el temporizador específico
        setRestTimers(prevTimers => {
          const updatedTimers = { ...prevTimers };
          if (updatedTimers[`${exerciseId}-${setIndex}`]) {
            updatedTimers[`${exerciseId}-${setIndex}`].isRunning = true;
          }
          return updatedTimers;
        });
      }
    }
  };

  // Función para resetear el temporizador de descanso
  const resetRestTimer = () => {
    if (activeRestTimer) {
      const { exerciseId, setIndex } = activeRestTimer;
      const currentExercise = exercises.find(ex => ex.id === exerciseId);
      const restTimeValue = currentExercise?.restTime || 60;
      
      clearInterval(timerRef.current);
      setTimeLeft(restTimeValue);
      setIsTimerRunning(false);
      
      // Reiniciar el temporizador
      startRestTimer(exerciseId, setIndex, restTimeValue);
    }
  };

  // Función para saltar el temporizador de descanso
  const skipRestTimer = () => {
    if (activeRestTimer) {
      const { exerciseId, setIndex } = activeRestTimer;
      
      clearInterval(timerRef.current);
      setIsTimerRunning(false);
      setActiveRestTimer(null);
      
      // Actualizar el tiempo total de descanso (añadir el tiempo que ya ha pasado)
      setTotalRestTime(prev => prev + (restTime - timeLeft));
      
      // Actualizar el temporizador específico
      const updatedTimers = { ...restTimers };
      if (updatedTimers[`${exerciseId}-${setIndex}`]) {
        updatedTimers[`${exerciseId}-${setIndex}`].isRunning = false;
        updatedTimers[`${exerciseId}-${setIndex}`].timeLeft = 0;
      }
      setRestTimers(updatedTimers);
    }
  };

  // Función para formatear el tiempo en formato mm:ss
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Función para formatear la duración total del entrenamiento
  const formatWorkoutTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${mins < 10 ? '0' : ''}${mins}:${secs < 10 ? '0' : ''}${secs}`;
    }
    
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Función para calcular el volumen total del entrenamiento
  const calculateTotalVolume = () => {
    let totalVolume = 0;
    
    Object.keys(setData).forEach(exerciseId => {
      const exerciseSets = setData[exerciseId] || [];
      
      exerciseSets.forEach(set => {
        if (set.completed) {
          totalVolume += set.weight * set.reps;
        }
      });
    });
    
    return totalVolume;
  };

  // Función para calcular las calorías quemadas
  const calculateCaloriesBurned = () => {
    let totalCalories = 0;
    
    Object.keys(setData).forEach(exerciseId => {
      const exerciseSets = setData[exerciseId] || [];
      const exercise = exercises.find(ex => ex.id === exerciseId);
      
      if (!exercise) return;
      
      exerciseSets.forEach(set => {
        if (set.completed) {
          // Calorías base por set completado
          const caloriesPerSet = exercise.caloriesPerSet || exercise.baseCalories || 5;
          
          // Factor de intensidad basado en el peso y RPE
          const weightFactor = set.weight > 0 ? (1 + (set.weight / 100)) : 1;
          const rpeFactor = set.rpe > 0 ? (1 + (set.rpe / 20)) : 1; // RPE contribuye hasta un 50% adicional
          
          // Calorías por set con factores
          const setCalories = caloriesPerSet * weightFactor * rpeFactor;
          
          totalCalories += setCalories;
        }
      });
    });
    
    // Tiempo activo = tiempo total - tiempo de descanso
    const activeTime = Math.max(0, workoutTime - totalRestTime);
    
    // Factor de intensidad basado en tiempo activo (calorías adicionales por minuto de actividad)
    const intensityFactor = 0.05; // 5% adicional por minuto de actividad
    const additionalCalories = (activeTime / 60) * intensityFactor * totalCalories;
    
    return Math.round(totalCalories + additionalCalories);
  };

  // Función para obtener músculos trabajados
  const getWorkoutMuscles = () => {
    const musclesSet = new Set();
    
    exercises.forEach(exercise => {
      const exerciseSets = completedSets[exercise.id] || [];
      const hasCompletedSets = exerciseSets.some(Boolean);
      
      if (hasCompletedSets && exercise.primaryMuscle) {
        musclesSet.add(exercise.primaryMuscle);
      }
      
      if (hasCompletedSets && exercise.secondaryMuscle) {
        musclesSet.add(exercise.secondaryMuscle);
      }
      
      if (hasCompletedSets && exercise.muscle) {
        musclesSet.add(exercise.muscle);
      }
    });
    
    return Array.from(musclesSet);
  };

  // Función para registrar el entrenamiento en la base de datos
  const logWorkout = async (stats) => {
    setLoading(true);
    
    try {
      // Crear un objeto con los datos del entrenamiento
      const workoutData = {
        date: new Date().toISOString().split('T')[0],
        duration: stats.duration,
        caloriesBurned: stats.caloriesBurned,
        totalVolume: stats.totalVolume,
        exercises: exercises.map(exercise => {
          const exerciseSets = setData[exercise.id] || [];
          const completedSets = exerciseSets.filter(set => set.completed);
          
          let exerciseVolume = 0;
          let totalReps = 0;
          
          completedSets.forEach(set => {
            exerciseVolume += set.weight * set.reps;
            totalReps += set.reps;
          });
          
          return {
            exerciseId: exercise.id,
            name: exercise.name,
            sets: completedSets.length,
            reps: totalReps,
            weight: exerciseSets.length > 0 ? Math.max(...exerciseSets.map(set => set.weight)) : 0,
            volume: exerciseVolume,
            caloriesBurned: completedSets.reduce((total, set) => {
              const caloriesPerSet = exercise.caloriesPerSet || exercise.baseCalories || 5;
              const weightFactor = set.weight > 0 ? (1 + (set.weight / 100)) : 1;
              const rpeFactor = set.rpe > 0 ? (1 + (set.rpe / 20)) : 1;
              return total + (caloriesPerSet * weightFactor * rpeFactor);
            }, 0),
            setDetails: completedSets.map(set => ({
              type: set.type,
              weight: set.weight,
              reps: set.reps,
              rpe: set.rpe
            }))
          };
        }).filter(ex => ex.sets > 0), // Solo incluir ejercicios con sets completados
      };
      
      // Llamar al servicio para registrar el entrenamiento
      const response = await agtfitService.logWorkout(workoutData);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to log workout');
      }
      
      return response.data;
    } catch (error) {
      console.error('Error logging workout:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Función para actualizar el perfil del usuario con las calorías quemadas
  const updateProfileWithCalories = async (caloriesBurned) => {
    if (!caloriesBurned || caloriesBurned <= 0) return;
    
    try {
      const date = new Date().toISOString().split('T')[0];
      const response = await agtfitService.updateProfileCalories({
        caloriesBurned,
        date
      });
      
      if (!response.success) {
        console.error('Error updating profile calories:', response.error);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error updating profile calories:', error);
      throw error;
    }
  };

  // Función para finalizar el entrenamiento
  const handleFinishWorkout = async () => {
    // Detener todos los temporizadores
    clearInterval(timerRef.current);
    clearInterval(workoutTimerRef.current);
    
    // Si hay un temporizador de descanso activo, añadir el tiempo restante al total
    if (activeRestTimer) {
      setTotalRestTime(prev => prev + (restTime - timeLeft));
    }
    
    // Calcular estadísticas finales
    const duration = workoutTime;
    const caloriesBurned = calculateCaloriesBurned();
    const musclesWorked = getWorkoutMuscles();
    const totalVolume = calculateTotalVolume();
    
    let totalSets = 0;
    let completedSetsCount = 0;
    
    // Contar sets totales y completados
    Object.values(completedSets).forEach(sets => {
      totalSets += sets.length;
      completedSetsCount += sets.filter(Boolean).length;
    });
    
    // Actualizar estadísticas
    const stats = {
      duration,
      caloriesBurned,
      exerciseCount: exercises.length,
      musclesWorked,
      totalSets,
      completedSets: completedSetsCount,
      totalVolume,
    };
    
    setWorkoutStats(stats);
    setWorkoutActive(false);
    setOpenWorkoutComplete(true);
    
    try {
      // Registrar el entrenamiento
      // const workoutResult = await logWorkout(stats);
      await logWorkout(stats);

      // Actualizar el perfil con las calorías quemadas
      await updateProfileWithCalories(stats.caloriesBurned);
  
      // Notificar al componente padre sobre las calorías quemadas y estadísticas
      if (onWorkoutComplete) {
        onWorkoutComplete(stats);
      }
  
      // Actualizar directamente TodaysActivity si la referencia existe
      if (todaysActivityRef && todaysActivityRef.current) {
        todaysActivityRef.current.updateCaloriesFromWorkout(stats.caloriesBurned);
      }
    } catch (error) {
      console.error('Error finalizing workout:', error);
    }
  };

  // Función para cerrar el diálogo de entrenamiento completado y volver
  const handleCloseWorkoutComplete = () => {
    setOpenWorkoutComplete(false);
    onBack();
  };

  // Función para manejar el descarte del entrenamiento
  const handleDiscardWorkout = () => {
    setOpenDiscardModal(true);
  };

  // Función para confirmar el descarte del entrenamiento
  const confirmDiscardWorkout = () => {
    // Detener todos los temporizadores
    clearInterval(timerRef.current);
    clearInterval(workoutTimerRef.current);
    
    setOpenDiscardModal(false);
    onBack();
  };

  // Obtener el RPE descripción basado en el valor
  const getRPEDescription = (rpeValue) => {
    switch (rpeValue) {
      case 6:
        return { title: 'Moderate effort', description: 'Could have done 4+ more reps' };
      case 7:
        return { title: 'Moderate-Hard effort', description: 'Could have done 3 more reps' };
      case 7.5:
        return { title: 'Getting Hard', description: 'Could have done 2-3 more reps' };
      case 8:
        return { title: 'Hard effort', description: 'Could have done 2 more reps' };
      case 8.5:
        return { title: 'Very Hard', description: 'Could have done 1-2 more reps' };
      case 9:
        return { title: 'Near Max effort', description: 'Could have done 1 more rep' };
      case 9.5:
        return { title: 'Very Near Max', description: 'Could maybe have done 1 more rep' };
      case 10:
        return { title: 'Max effort', description: 'Could not have done more reps' };
      default:
        return { title: '', description: '' };
    }
  };

  // Componente mejorado para el temporizador de descanso
  const RestTimerCard = () => {
    const isScrolled = useScrollTrigger({
      disableHysteresis: true,
      threshold: 80,
    });

    // Estilo para versión compacta (48px)
    const compactStyle = {
      height: '48px',
      padding: 0,
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      borderRadius: '0',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      background: GRADIENT,
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
    };

    // Estilo para versión normal
    const normalStyle = {
      padding: theme.spacing(2.5),
      borderRadius: '16px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      background: theme.palette.mode === 'dark' 
        ? 'linear-gradient(180deg, rgba(255,107,0,0.15) 0%, rgba(0,0,0,0) 100%)' 
        : 'linear-gradient(180deg, rgba(255,107,0,0.05) 0%, rgba(255,255,255,0) 100%)',
      boxShadow: 'none'
    };

    return (
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backdropFilter: 'blur(10px)',
          backgroundColor: theme.palette.mode === 'dark' 
            ? isScrolled ? 'transparent' : 'rgba(18, 18, 18, 0.8)' 
            : isScrolled ? 'transparent' : 'rgba(255, 255, 255, 0.8)',
          mb: 2,
          transition: 'all 0.3s ease',
          overflow: 'hidden',
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)'}`,
          ...(isScrolled ? compactStyle : normalStyle)
        }}
      >
        {!isScrolled ? (
          // Versión completa del timer
          <>
            <Typography 
              variant="subtitle1" 
              align="center" 
              sx={{ 
                fontWeight: 500, 
                color: theme.palette.text.secondary,
                mb: 1
              }}
            >
              Rest Time
            </Typography>
            
            <Typography 
              variant="h3" 
              align="center" 
              sx={{ 
                fontWeight: 700, 
                fontSize: '2.5rem',
                background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-1px'
              }}
            >
              {formatTime(timeLeft)}
            </Typography>
            
            <LinearProgress 
              variant="determinate" 
              value={(timeLeft / restTime) * 100} 
              sx={{ 
                my: 2, 
                height: 6, 
                borderRadius: 3,
                width: '100%',
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                '& .MuiLinearProgress-bar': {
                  background: GRADIENT,
                }
              }}
            />
            
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: 1.5
            }}>
              <Button
                variant="contained"
                onClick={toggleRestTimer}
                sx={{ 
                  borderRadius: '20px',
                  textTransform: 'none',
                  py: 1,
                  px: 2.5,
                  fontWeight: 500,
                  background: GRADIENT,
                  color: '#FFFFFF',
                  boxShadow: 'none',
                  '&:hover': {
                    background: GRADIENT,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {isTimerRunning ? (
                    <Pause size={18} style={{ marginRight: '6px' }} />
                  ) : (
                    <Play size={18} style={{ marginRight: '6px' }} />
                  )}
                  {isTimerRunning ? 'Pause' : 'Resume'}
                </Box>
              </Button>
              
              <Button
                variant="contained"
                onClick={resetRestTimer}
                sx={{ 
                  borderRadius: '20px',
                  textTransform: 'none',
                  py: 1,
                  px: 2.5,
                  fontWeight: 500,
                  background: GRADIENT,
                  color: '#FFFFFF',
                  boxShadow: 'none',
                  '&:hover': {
                    background: GRADIENT,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <RotateCcw size={18} style={{ marginRight: '6px' }} />
                  Reset
                </Box>
              </Button>
              
              <Button
                variant="contained"
                onClick={skipRestTimer}
                sx={{ 
                  borderRadius: '20px',
                  textTransform: 'none',
                  py: 1,
                  px: 2.5,
                  fontWeight: 500,
                  backgroundColor: theme.palette.mode === 'dark' ? '#3D2222' : '#FFEFEF',
                  color: '#FF3B30',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' ? '#4D2222' : '#FFE0E0',
                    boxShadow: 'none'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <X size={18} style={{ marginRight: '6px' }} />
                  Skip
                </Box>
              </Button>
            </Box>
            
            <Box sx={{ 
              mt: 2.5, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              pb: 1
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mr: 2, 
                  fontSize: '0.85rem', 
                  color: theme.palette.text.secondary 
                }}
              >
                Adjust Rest Time:
              </Typography>
              <TextField
                type="number"
                size="small"
                value={restTime}
                onChange={(e) => handleAdjustRestTime(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">s</InputAdornment>,
                  inputProps: { min: 0, max: 300, step: 5 },
                  sx: {
                    borderRadius: '12px',
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255,255,255,0.05)' 
                      : '#F5F5F7',
                    '& fieldset': {
                      borderColor: 'transparent'
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.divider
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: FIT_ORANGE,
                      borderWidth: 1
                    }
                  }
                }}
                sx={{ 
                  width: 100,
                  '& .MuiOutlinedInput-root': {
                    height: '36px'
                  }
                }}
              />
            </Box>
          </>
        ) : (
          // Versión compacta del timer (48px exactos)
          <Box sx={{ 
            width: '100%', 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '48px'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  fontWeight: 600, 
                  color: '#FFFFFF',
                  mr: 1
                }}
              >
                REST
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700, 
                  fontSize: '1.1rem',
                  color: '#FFFFFF'
                }}
              >
                {formatTime(timeLeft)}
              </Typography>
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1
            }}>
              <Button
                size="small"
                onClick={toggleRestTimer}
                sx={{ 
                  minWidth: '32px',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  padding: 0,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.3)',
                  }
                }}
              >
                {isTimerRunning ? <Pause size={14} /> : <Play size={14} />}
              </Button>
              
              <Button
                size="small"
                onClick={resetRestTimer}
                sx={{ 
                  minWidth: '32px',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  padding: 0,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.3)',
                  }
                }}
              >
                <RotateCcw size={14} />
              </Button>
              
              <Button
                size="small"
                onClick={skipRestTimer}
                sx={{ 
                  minWidth: '32px',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  padding: 0,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.3)',
                  }
                }}
              >
                <X size={14} />
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <WorkoutContainer>
      <TopBar>
      {/* Fila 1: Chevron izquierdo y título del entrenamiento/rutina */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton 
          onClick={workoutActive ? handleDiscardWorkout : onBack}
          sx={{ mr: 2, color: FIT_ORANGE }}
        >
          <ChevronLeft size={24} />
        </IconButton>
        <Typography
          variant="h5"
          fontWeight={600}
          sx={{
            color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit'
          }}
        >
          {selectedRoutine ? selectedRoutine.name : 'Workout'}
        </Typography>
      </Box>
    
      {/* Fila 2: Tiempo, volumen y calorías/sets */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'center', gap: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography 
            variant="body1" 
            fontWeight={600}
            sx={{
              background: GRADIENT,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {formatWorkoutTime(workoutTime)}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.textSecondary : 'text.secondary'
            }}
          >
            Time
          </Typography>
        </Box>
    
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography 
            variant="body1" 
            fontWeight={600}
            sx={{
              background: GRADIENT,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {calculateTotalVolume()} {weightUnit}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.textSecondary : 'text.secondary'
            }}
          >
            Volume
          </Typography>
        </Box>
    
        {/* Elemento clickeable para alternar entre sets y calorías */}
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            cursor: 'pointer'
          }}
          onClick={() => setShowSets(!showSets)}
        >
          <Typography 
            variant="body1" 
            fontWeight={600}
            sx={{
              background: GRADIENT,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {showSets 
              ? exercises.reduce((total, ex) => {
                  const sets = completedSets[ex.id] || [];
                  return total + sets.filter(Boolean).length;
                }, 0)
              : calculateCaloriesBurned()
            }
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.textSecondary : 'text.secondary'
            }}
          >
            {showSets ? 'Sets' : 'Cal'}
          </Typography>
        </Box>
      </Box>
    
      {/* Fila 3: Botones Discard y Finish */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {workoutActive && (
          <>
            <Button
              variant="text"
              onClick={handleDiscardWorkout}
              sx={{
                color: APPLE_RED, // Cambiado a APPLE_RED
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.9rem',
              }}
            >
              Discard
            </Button>
            <Button
              variant="contained"
              onClick={handleFinishWorkout}
              sx={{
                borderRadius: 8,
                background: GRADIENT,
                boxShadow: 'none',
                textTransform: 'none',
                color: '#FFFFFF', // Texto blanco en ambos modos
                '&:hover': {
                  background: 'linear-gradient(90deg, #FF8800 0%, #FF5500 100%)',
                  boxShadow: 'none',
                },
              }}
            >
              Finish
            </Button>
          </>
        )}
      </Box>
    </TopBar>
      
      {/* Temporizador de descanso activo */}
      {activeRestTimer && <RestTimerCard />}
      
      {/* Lista de ejercicios */}
      {exercises.length > 0 ? (
        exercises.map((exercise, index) => (
          <ExerciseCard key={`${exercise.id}-${index}`}>
            <ExerciseHeader>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box 
                  sx={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: GRADIENT,
                    mr: 2
                  }}
                >
                  <Dumbbell size={20} color="#FFFFFF" />
                </Box>
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{
                      color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit'
                    }}
                  >
                    {exercise.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.textSecondary : 'text.secondary'
                    }}
                  >
                    {exercise.muscle || exercise.primaryMuscle || 'No muscle specified'}
                  </Typography>
                </Box>
              </Box>
              
              <Chip 
                label={`${completedSets[exercise.id]?.filter(Boolean).length || 0}/${exercise.sets} sets`}
                sx={{ 
                  fontWeight: 500,
                  background: GRADIENT, // Fondo con gradiente naranja
                  color: '#FFFFFF', // Texto blanco en ambos modos
                }}
              />
            </ExerciseHeader>
            
            {/* Encabezados de columnas */}
            <Box sx={{ 
              display: 'flex', 
              px: 2, 
              py: 1, 
              borderBottom: `1px solid ${theme.palette.divider}` 
            }}>
              <Box sx={{ width: '20%' }}>
                <SetHeaderItem>Set</SetHeaderItem>
              </Box>
              <Box sx={{ width: '20%' }}>
                <SetHeaderItem>Previous</SetHeaderItem>
              </Box>
              <Box sx={{ width: '15%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {/* Reemplazar esto: */}
                {/* <SetHeaderItem>
                  KG
                  <IconButton
                    size="small"
                    onClick={() => setOpenWeightModal(true)}
                    sx={{ ml: 0.5, p: 0 }}
                  >
                    <Scale size={12} />
                  </IconButton>
                </SetHeaderItem> */}
            
                {/* Por esto: */}
                <Typography 
                  variant="body2" 
                  component="span"
                  onClick={() => setOpenWeightModal(true)}
                  sx={{ 
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    color: FIT_ORANGE,
                    textTransform: 'uppercase',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: GRADIENT,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    '&:hover': {
                      opacity: 0.8
                    }
                  }}
                >
                  {weightUnit === 'kg' ? 'KG' : 'LBS'}
                </Typography>
              </Box>
              <Box sx={{ width: '15%' }}>
                <SetHeaderItem>Reps</SetHeaderItem>
              </Box>
              <Box sx={{ width: '15%' }}>
                <SetHeaderItem>RPE</SetHeaderItem>
              </Box>
              <Box sx={{ width: '15%', display: 'flex', justifyContent: 'center' }}>
                <SetHeaderItem>✓</SetHeaderItem>
              </Box>
            </Box>
            
            <List disablePadding sx={{ px: 2, py: 1 }}>
              {Array.from({ length: exercise.sets }).map((_, setIndex) => {
                const setDataItem = setData[exercise.id]?.[setIndex] || {
                  type: 'N',
                  weight: 0,
                  reps: 10,
                  rpe: 0,
                  completed: false,
                  previousWeight: 0,
                };
            
                const isCompleted = completedSets[exercise.id]?.[setIndex] || false;
                const isResting = restTimers[`${exercise.id}-${setIndex}`]?.isRunning;
            
                return (
                  <SetItem key={setIndex} isRest={isResting} setType={setDataItem.type}>
                    {/* Set Type Column */}
                    <Box sx={{ width: '20%', display: 'flex', alignItems: 'center' }}>
                      <SetTypeChip 
                        setType={setDataItem.type}
                        onClick={() => handleOpenSetTypeModal(exercise.id, setIndex)}
                      >
                        {setDataItem.type === 'W' ? 'W' : 
                        setDataItem.type === 'F' ? 'F' : 
                        setDataItem.type === 'D' ? 'D' : 
                        setIndex + 1}
                      </SetTypeChip>
                    </Box>
            
                    {/* Previous Weight Column */}
                    <Box sx={{ width: '20%' }}>
                      <Typography variant="body2" color="text.secondary">
                        {setDataItem.previousWeight > 0 ? `${setDataItem.previousWeight} ${weightUnit}` : '-'}
                      </Typography>
                    </Box>
            
                    {/* Weight Column - Ahora como input normal */}
                    <Box sx={{ width: '15%' }}>
                      <TextField
                        type="number"
                        size="small"
                        value={setDataItem.weight}
                        onChange={(e) => {
                          const updatedSetData = { ...setData };
                          if (updatedSetData[exercise.id] && updatedSetData[exercise.id][setIndex]) {
                            updatedSetData[exercise.id][setIndex].weight = parseFloat(e.target.value) || 0;
                            setSetData(updatedSetData);
                          }
                        }}
                        InputProps={{
                          inputProps: { min: 0, max: 999 }
                        }}
                        sx={{ 
                          width: 60,
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: 'transparent',
                            },
                            '&:hover fieldset': {
                              borderColor: theme.palette.divider,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: theme.palette.primary.main,
                              borderWidth: 1,
                            },
                          },
                          '& .MuiInputBase-input': {
                            textAlign: 'center',
                            p: 0.5,
                          },
                        }}
                      />
                    </Box>
            
                    {/* Reps Column */}
                    <Box sx={{ width: '15%' }}>
                      <TextField
                        type="number"
                        size="small"
                        value={setDataItem.reps}
                        onChange={(e) => handleUpdateReps(exercise.id, setIndex, e.target.value)}
                        InputProps={{
                          inputProps: { min: 0, max: 999 }
                        }}
                        sx={{ 
                          width: 60,
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: 'transparent',
                            },
                            '&:hover fieldset': {
                              borderColor: theme.palette.divider,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: theme.palette.primary.main,
                              borderWidth: 1,
                            },
                          },
                          '& .MuiInputBase-input': {
                            textAlign: 'center',
                            p: 0.5,
                          },
                        }}
                      />
                    </Box>
            
                    {/* RPE Column */}
                    <Box sx={{ 
                      width: '15%', 
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: 'center' // Añadimos justifyContent: 'center' para centrar
                    }}>
                      <Button 
                        variant="text" 
                        size="small"
                        onClick={() => handleOpenRPEModal(exercise.id, setIndex)}
                        sx={{ 
                          minWidth: 'auto', 
                          p: 0,
                          color: theme.palette.text.primary,
                          textAlign: 'center', // Añadimos textAlign: 'center'
                          width: '100%' // Añadimos width: '100%' para que ocupe todo el espacio
                        }}
                      >
                        {setDataItem.rpe > 0 ? setDataItem.rpe : '-'}
                      </Button>
                    </Box>
            
                    {/* Check Column */}
                    <Box sx={{ width: '15%', display: 'flex', justifyContent: 'center' }}>
                      <Checkbox
                        checked={isCompleted}
                        onChange={() => handleSetCheck(exercise.id, setIndex)}
                        color="primary"
                        icon={<Box 
                          sx={{ 
                            width: 24, 
                            height: 24, 
                            borderRadius: '50%', 
                            border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)'}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }} 
                        />}
                        checkedIcon={<Box 
                          sx={{ 
                            width: 24, 
                            height: 24, 
                            borderRadius: '50%', 
                            background: GRADIENT,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }} 
                        >
                          <Check size={16} color="#FFFFFF" />
                        </Box>}
                      />
                    </Box>
                  </SetItem>
                );
              })}
            </List>
            
            {/* Botón para añadir set */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', // Cambio a center para centrarlos
              alignItems: 'center',
              py: 1.5, 
              px: 2,
              gap: 2 // Añadido gap para separación entre botones
            }}>
              <Button
                onClick={() => handleDeleteExercise(exercise.id)}
                startIcon={<X size={16} />}
                sx={{ 
                  textTransform: 'none',
                  color: APPLE_RED, // Color rojo de Apple
                }}
              >
                Delete exercise
              </Button>
              <Button
                onClick={() => handleAddSet(exercise.id)}
                startIcon={<Plus size={16} />}
                sx={{ 
                  textTransform: 'none',
                  color: theme.palette.mode === 'dark' ? FIT_ORANGE : FIT_ORANGE,
                }}
              >
                Add set
              </Button>
            </Box>
          </ExerciseCard>
        ))
      ) : (
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            py: 8,
            textAlign: 'center'
          }}
        >
          <Dumbbell size={48} color={theme.palette.text.disabled} />
          <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
            No exercises added yet
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 1, mb: 3 }}>
            Tap the + button to add exercises
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Plus size={16} />}
            onClick={() => setOpenExerciseExplorer(true)}
            sx={{ 
              borderRadius: 8,
              background: GRADIENT,
              boxShadow: 'none',
              textTransform: 'none',
              color: '#FFFFFF', // Texto blanco en ambos modos
              '&:hover': {
                background: 'linear-gradient(90deg, #FF8800 0%, #FF5500 100%)',
                boxShadow: 'none',
              },
            }}
          >
           Add exercise
          </Button>
        </Box>
      )}
      
      {/* Botón flotante para añadir ejercicios */}
      {exercises.length > 0 && workoutActive && (
        <AddExerciseFab color="primary" onClick={() => setOpenExerciseExplorer(true)}>
          <Plus size={24} />
        </AddExerciseFab>
      )}
      
      {/* Modal de tipo de set */}
      <Dialog
        open={openSetTypeModal}
        onClose={() => setOpenSetTypeModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : '0 4px 30px rgba(0,0,0,0.15)',
            padding: '8px',
            background: theme.palette.actualMode === 'wallpaper'
              ? WALLPAPER_COLORS.bg
              : (theme.palette.mode === 'dark'
                ? 'rgba(18, 18, 18, 0.95)'
                : 'rgba(255, 255, 255, 0.95)'),
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{
          textAlign: 'center',
          fontSize: '20px',
          fontWeight: 600,
          pb: 1,
          pt: 2,
          color: theme.palette.actualMode === 'wallpaper'
            ? WALLPAPER_COLORS.text
            : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
          position: 'relative',
          borderBottom: theme.palette.actualMode === 'wallpaper'
            ? `1px solid ${WALLPAPER_COLORS.border}`
            : `1px solid ${theme.palette.divider}`
        }}>
          Set Type
          <IconButton
            onClick={() => setOpenSetTypeModal(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.text.secondary,
              width: 36,
              height: 36,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255,255,255,0.05)' 
                  : 'rgba(0,0,0,0.03)'
              }
            }}
          >
            <X size={20} />
          </IconButton>
        </DialogTitle>
      
        <DialogContent sx={{ px: 1, py: 2 }}>
          <List>
            <ListItem 
              button 
              onClick={() => handleSelectSetType('W')}
              sx={{
                borderRadius: '14px',
                mb: 1.5,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255,255,255,0.05)' 
                    : 'rgba(0,0,0,0.03)',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              <ListItemText 
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      background: 'linear-gradient(135deg, #FFB74D 0%, #FFA726 100%)',
                      color: '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      mr: 2,
                      boxShadow: '0 2px 6px rgba(255,183,77,0.3)',
                      fontSize: '16px'
                    }}>
                      W
                    </Box>
                    <Box sx={{ fontWeight: 500 }}>
                      Warm Up Set
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        Prepare your muscles before working sets
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </ListItem>
      
            <ListItem 
              button 
              onClick={() => handleSelectSetType('N')}
              sx={{
                borderRadius: '14px',
                mb: 1.5,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255,255,255,0.05)' 
                    : 'rgba(0,0,0,0.03)',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              <ListItemText 
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      background: GRADIENT,
                      color: '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      mr: 2,
                      boxShadow: '0 2px 6px rgba(255,107,0,0.3)',
                      fontSize: '16px'
                    }}>
                      {currentSetIndex !== null ? currentSetIndex + 1 : ''}
                    </Box>
                    <Box sx={{ fontWeight: 500 }}>
                      Normal Set
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        Standard working set
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </ListItem>
      
            <ListItem 
              button 
              onClick={() => handleSelectSetType('F')}
              sx={{
                borderRadius: '14px',
                mb: 1.5,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255,255,255,0.05)' 
                    : 'rgba(0,0,0,0.03)',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              <ListItemText 
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      background: 'linear-gradient(135deg, #FF5252 0%, #FF3B30 100%)',
                      color: '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      mr: 2,
                      boxShadow: '0 2px 6px rgba(255,59,48,0.3)',
                      fontSize: '16px'
                    }}>
                      F
                    </Box>
                    <Box sx={{ fontWeight: 500 }}>
                      Failure Set
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        Set performed until muscle failure
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </ListItem>
      
            <ListItem 
              button 
              onClick={() => handleSelectSetType('D')}
              sx={{
                borderRadius: '14px',
                mb: 1.5,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255,255,255,0.05)' 
                    : 'rgba(0,0,0,0.03)',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              <ListItemText 
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      background: 'linear-gradient(135deg, #2196F3 0%, #007AFF 100%)',
                      color: '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      mr: 2,
                      boxShadow: '0 2px 6px rgba(0,122,255,0.3)',
                      fontSize: '16px'
                    }}>
                      D
                    </Box>
                    <Box sx={{ fontWeight: 500 }}>
                      Drop Set
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        Reduce weight and continue immediately
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </ListItem>
      
            {currentSetIndex !== null && currentExerciseId && setData[currentExerciseId] && 
            setData[currentExerciseId].length > 1 && (
              <ListItem 
                button 
                onClick={() => {
                  handleRemoveSet(currentExerciseId, currentSetIndex);
                  setOpenSetTypeModal(false);
                }}
                sx={{
                  borderRadius: '14px',
                  mb: 1,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255,59,48,0.1)',
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                <ListItemText 
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', color: '#FF3B30' }}>
                      <Box sx={{ 
                        width: 40, 
                        height: 40, 
                        borderRadius: '50%', 
                        background: 'linear-gradient(135deg, rgba(255,82,82,0.2) 0%, rgba(255,59,48,0.2) 100%)',
                        border: '1px solid rgba(255,59,48,0.3)',
                        color: '#FF3B30',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2
                      }}>
                        <X size={20} />
                      </Box>
                      <Box sx={{ fontWeight: 500 }}>
                        Remove Set
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'rgba(255,59,48,0.7)',
                            mt: 0.5
                          }}
                        >
                          Delete this set from the exercise
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              </ListItem>
            )}
          </List>
        </DialogContent>
      
        <DialogActions sx={{ 
          justifyContent: 'center', 
          pb: 2.5,
          pt: 1.5,
          px: 2,
          borderTop: `1px solid ${theme.palette.divider}`
        }}>
          <Button 
            onClick={() => setOpenSetTypeModal(false)}
            sx={{ 
              textTransform: 'none',
              borderRadius: '14px',
              px: 4,
              py: 1.2,
              background: GRADIENT,
              color: '#FFFFFF',
              fontWeight: 600,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              '&:hover': {
                background: GRADIENT,
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.2s ease',
              minWidth: '120px'
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Modal de peso - ahora con el diseño mejorado */}
      <Dialog 
        open={openWeightModal} 
        onClose={() => setOpenWeightModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 4px 30px rgba(0,0,0,0.15)',
            padding: '8px',
            background: theme.palette.mode === 'dark' 
              ? 'rgba(18, 18, 18, 0.95)' 
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          fontSize: '20px', 
          fontWeight: 600,
          pb: 1,
          color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
          position: 'relative'
        }}>
          Weight Unit
          <IconButton
            onClick={() => setOpenWeightModal(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.text.secondary
            }}
          >
            <X size={20} />
          </IconButton>
        </DialogTitle>
      
        <DialogContent>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: 3, 
              textAlign: 'center',
              px: 1,
              lineHeight: 1.6
            }}
          >
            Select the unit to use for weight measurements.
          </Typography>
      
          <Box 
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 2
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: '240px',
                mb: 1,
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <Select
                value={weightUnit}
                onChange={(e) => setWeightUnit(e.target.value)}
                sx={{
                  width: '120px',
                  height: '50px',
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    color: FIT_ORANGE
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: `rgba(255, 107, 0, 0.3)`,
                    borderRadius: '12px'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: FIT_ORANGE
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: FIT_ORANGE,
                    borderWidth: '2px'
                  }
                }}
              >
                <MenuItem value="kg">kg</MenuItem>
                <MenuItem value="lbs">lbs</MenuItem>
              </Select>
            </Box>
      
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ 
                display: 'block', 
                mt: 2,
                textAlign: 'center',
                fontWeight: 500
              }}
            >
              This will be used as the default unit for all weights
            </Typography>
          </Box>
      
          <Box 
            sx={{
              mt: 2,
              p: 2,
              background: 'linear-gradient(180deg, rgba(255,107,0,0.15) 0%, rgba(255,107,0,0.05) 100%)',
              borderRadius: '16px',
              border: `1px solid ${theme.palette.mode === 'dark' 
                ? 'rgba(255,107,0,0.2)' 
                : 'rgba(255,107,0,0.15)'}`,
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: theme.palette.text.secondary,
                fontSize: '0.85rem'
              }}
            >
              <Box component="span" sx={{ fontWeight: 600, color: FIT_ORANGE }}>Note: </Box>
              Changing the weight unit does not automatically convert existing weight values. Make sure to adjust your weights accordingly.
            </Typography>
          </Box>
        </DialogContent>
      
        <DialogActions sx={{ 
          justifyContent: 'space-between',
          px: 3,
          py: 2.5
        }}>
          <Button 
            onClick={() => setOpenWeightModal(false)}
            sx={{ 
              textTransform: 'none',
              fontWeight: 500,
              color: theme.palette.text.secondary,
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(255,255,255,0.05)' 
                : 'rgba(0,0,0,0.05)',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255,255,255,0.1)' 
                  : 'rgba(0,0,0,0.08)'
              },
              borderRadius: '12px',
              px: 3,
              py: 1
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => {
              setWeightUnit(weightUnit);
              setOpenWeightModal(false);
            }}
            sx={{ 
              textTransform: 'none',
              fontWeight: 600,
              background: GRADIENT,
              color: '#FFFFFF',
              '&:hover': {
                background: GRADIENT,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              },
              borderRadius: '12px',
              px: 4,
              py: 1,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Modal de RPE */}
      <Dialog 
        open={openRPEModal} 
        onClose={() => setOpenRPEModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 4px 30px rgba(0,0,0,0.15)',
            padding: '8px',
            background: theme.palette.mode === 'dark' 
              ? 'rgba(18, 18, 18, 0.95)' 
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          fontSize: '20px', 
          fontWeight: 600,
          pb: 1,
          color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
          position: 'relative'
        }}>
          Log Set RPE
          <IconButton
            onClick={() => setOpenRPEModal(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.text.secondary
            }}
          >
            <X size={20} />
          </IconButton>
        </DialogTitle>
      
        <DialogContent>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography 
              variant="h2" 
              fontWeight={700} 
              sx={{ 
                mb: 1, 
                background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-1px',
                fontSize: '3.5rem'
              }}
            >
              {setData[currentExerciseId]?.[currentSetIndex]?.rpe || 0}
            </Typography>
            <Typography 
              variant="subtitle1" 
              color="text.secondary"
              sx={{ fontWeight: 500, letterSpacing: '0.5px' }}
            >
              RPE
            </Typography>
          </Box>
      
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            {[6, 7, 7.5, 8, 8.5, 9, 9.5, 10].map((rpe) => (
              <Grid item xs={3} key={rpe}>
                <Button
                  onClick={() => {
                    // Actualizar temporalmente el valor RPE en el estado local
                    const updatedSetData = { ...setData };
                    if (updatedSetData[currentExerciseId] && updatedSetData[currentExerciseId][currentSetIndex]) {
                      updatedSetData[currentExerciseId][currentSetIndex].rpe = rpe;
                      setSetData(updatedSetData);
                    }
                  }}
                  sx={{
                    width: '100%',
                    height: '60px',
                    borderRadius: '14px',
                    background: setData[currentExerciseId]?.[currentSetIndex]?.rpe === rpe 
                      ? GRADIENT
                      : theme.palette.mode === 'dark' 
                        ? 'rgba(255,255,255,0.08)'
                        : 'rgba(0,0,0,0.05)',
                    color: setData[currentExerciseId]?.[currentSetIndex]?.rpe === rpe 
                      ? '#FFFFFF' 
                      : theme.palette.text.primary,
                    '&:hover': {
                      background: setData[currentExerciseId]?.[currentSetIndex]?.rpe === rpe 
                        ? GRADIENT
                        : theme.palette.mode === 'dark' 
                          ? 'rgba(255,255,255,0.12)'
                          : 'rgba(0,0,0,0.08)',
                      boxShadow: setData[currentExerciseId]?.[currentSetIndex]?.rpe === rpe 
                        ? '0 2px 8px rgba(0,0,0,0.15)' 
                        : 'none',
                    },
                    transition: 'all 0.2s ease',
                    fontWeight: 600,
                    fontSize: '18px',
                    boxShadow: setData[currentExerciseId]?.[currentSetIndex]?.rpe === rpe 
                      ? '0 2px 8px rgba(0,0,0,0.15)' 
                      : 'none',
                  }}
                >
                  {rpe}
                </Button>
              </Grid>
            ))}
          </Grid>
      
          {setData[currentExerciseId]?.[currentSetIndex]?.rpe > 0 && (
            <Box sx={{ 
              textAlign: 'center', 
              p: 2.5, 
              background: 'linear-gradient(180deg, rgba(255,107,0,0.15) 0%, rgba(255,107,0,0.05) 100%)',
              borderRadius: '16px',
              border: `1px solid ${theme.palette.mode === 'dark' 
                ? 'rgba(255,107,0,0.2)' 
                : 'rgba(255,107,0,0.15)'}`,
              backdropFilter: 'blur(5px)'
            }}>
              <Typography 
                variant="subtitle1" 
                fontWeight={600} 
                sx={{ 
                  mb: 0.5,
                  background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {getRPEDescription(setData[currentExerciseId][currentSetIndex].rpe).title}
              </Typography>
              <Typography 
                variant="body2"
                sx={{ lineHeight: 1.6, color: 'text.secondary' }}
              >
                {getRPEDescription(setData[currentExerciseId][currentSetIndex].rpe).description}
              </Typography>
            </Box>
          )}
        </DialogContent>
      
        <DialogActions sx={{ 
          justifyContent: 'space-between',
          px: 3,
          py: 2.5
        }}>
          <Button 
            onClick={() => setOpenRPEModal(false)}
            sx={{ 
              textTransform: 'none',
              fontWeight: 500,
              color: theme.palette.text.secondary,
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(255,255,255,0.05)' 
                : 'rgba(0,0,0,0.05)',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255,255,255,0.1)' 
                  : 'rgba(0,0,0,0.08)'
              },
              borderRadius: '12px',
              px: 3,
              py: 1
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => {
              handleSelectRPE(setData[currentExerciseId]?.[currentSetIndex]?.rpe || 0);
            }}
            disabled={!setData[currentExerciseId]?.[currentSetIndex]?.rpe}
            sx={{ 
              textTransform: 'none',
              fontWeight: 600,
              background: GRADIENT,
              color: '#FFFFFF',
              '&:hover': {
                background: GRADIENT,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              },
              '&.Mui-disabled': {
                background: theme.palette.mode === 'dark'
                  ? 'rgba(255,107,0,0.3)'
                  : 'rgba(255,107,0,0.4)',
                color: 'rgba(255,255,255,0.6)'
              },
              borderRadius: '12px',
              px: 4,
              py: 1,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Modal de descartar entrenamiento */}
      <Dialog
        open={openDiscardModal}
        onClose={() => setOpenDiscardModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
            padding: '16px',
            background: theme.palette.mode === 'dark' 
              ? 'rgba(18, 18, 18, 0.95)' 
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogContent sx={{ pt: 4, pb: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #FF5252 0%, #FF3B30 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                mb: 2,
                boxShadow: '0 4px 15px rgba(255,59,48,0.25)',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: -4,
                  borderRadius: '50%',
                  background: 'rgba(255,59,48,0.2)',
                  zIndex: -1
                }
              }}
            >
              <AlertTriangle size={32} color="#FFFFFF" />
            </Box>
            
            <Typography 
              variant="h5" 
              align="center" 
              gutterBottom
              sx={{
                fontWeight: 700,
                mb: 1.5,
                background: 'linear-gradient(135deg, #FF5252 0%, #FF3B30 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Discard Workout?
            </Typography>
            
            <Typography 
              variant="body1" 
              color="text.secondary" 
              align="center"
              sx={{ maxWidth: '85%', mx: 'auto' }}
            >
              Are you sure you want to discard this workout? All your progress will be lost.
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions 
          sx={{ 
            justifyContent: 'center', 
            pb: 3, 
            pt: 1,
            gap: 1.5
          }}
        >
          <Button
            onClick={() => setOpenDiscardModal(false)}
            sx={{ 
              borderRadius: '14px',
              textTransform: 'none',
              minWidth: 120,
              py: 1.2,
              px: 3,
              fontWeight: 600,
              background: theme.palette.mode === 'dark' 
                ? 'rgba(255,255,255,0.1)' 
                : 'rgba(0,0,0,0.05)',
              color: theme.palette.text.primary,
              border: 'none',
              '&:hover': {
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(255,255,255,0.15)' 
                  : 'rgba(0,0,0,0.08)',
                transform: 'translateY(-1px)',
                boxShadow: theme.palette.mode === 'dark' 
                  ? '0 4px 12px rgba(255,255,255,0.05)' 
                  : '0 4px 12px rgba(0,0,0,0.05)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            Cancel
          </Button>
          
          <Button
            onClick={confirmDiscardWorkout}
            sx={{ 
              borderRadius: '14px',
              textTransform: 'none',
              minWidth: 120,
              py: 1.2,
              px: 3,
              fontWeight: 600,
              background: 'linear-gradient(135deg, #FF5252 0%, #FF3B30 100%)',
              color: '#FFFFFF',
              boxShadow: '0 4px 12px rgba(255,59,48,0.25)',
              '&:hover': {
                background: 'linear-gradient(135deg, #FF5252 0%, #FF3B30 100%)',
                boxShadow: '0 6px 15px rgba(255,59,48,0.3)',
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Explorador de ejercicios */}
      <RoutineExerciseExplorer 
        open={openExerciseExplorer} 
        onClose={() => setOpenExerciseExplorer(false)}
        onAddSelectedExercises={handleAddExercises}
      />
      
      {/* Diálogo de entrenamiento completado */}
      <Dialog
        open={openWorkoutComplete}
        onClose={handleCloseWorkoutComplete}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : '0 8px 40px rgba(0,0,0,0.15)',
            padding: 0,
            background: theme.palette.actualMode === 'wallpaper'
              ? WALLPAPER_COLORS.bg
              : (theme.palette.mode === 'dark'
                ? 'rgba(18, 18, 18, 0.95)'
                : 'rgba(255, 255, 255, 0.95)'),
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '90vh',
          }
        }}
      >
        {/* Header - Fixed */}
        <Box sx={{
          textAlign: 'center',
          pb: 2,
          pt: 3,
          px: 3,
          borderBottom: theme.palette.actualMode === 'wallpaper'
            ? `1px solid ${WALLPAPER_COLORS.border}`
            : `1px solid ${theme.palette.divider}`
        }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            mb: 2 
          }}>
            <Box 
              sx={{ 
                width: 70, 
                height: 70, 
                borderRadius: '50%', 
                background: GRADIENT,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 20px rgba(255,107,0,0.25)',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: -4,
                  borderRadius: '50%',
                  background: 'linear-gradient(90deg, rgba(255,152,0,0.3) 0%, rgba(255,107,0,0.3) 100%)',
                  zIndex: -1
                }
              }}
            >
              <Trophy size={32} color="#FFFFFF" />
            </Box>
          </Box>
      
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700,
              background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1
            }}
          >
            Workout Complete!
          </Typography>
      
          <Typography
            variant="subtitle1"
            sx={{
              maxWidth: '80%',
              mx: 'auto',
              color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.textSecondary : 'text.secondary'
            }}
          >
            Great job! You've completed your workout session.
          </Typography>
        </Box>
      
        {/* Scrollable Content */}
        <Box 
          sx={{ 
            px: { xs: 2, sm: 3 }, 
            py: 3,
            overflowY: 'auto',
            flexGrow: 1,
            '&::-webkit-scrollbar': {
              width: '4px',
              height: '4px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.2)'
                : 'rgba(0,0,0,0.1)',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.3)'
                : 'rgba(0,0,0,0.2)',
            }
          }}
        >
          <Box 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr 1fr', 
              gap: 2, 
              mb: 3,
            }}
          >
            <Box
              sx={{
                borderRadius: '16px',
                p: 2,
                background: theme.palette.actualMode === 'wallpaper'
                  ? WALLPAPER_COLORS.bgLight
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.05)'
                    : 'rgba(0,0,0,0.02)'),
                border: theme.palette.actualMode === 'wallpaper'
                  ? `1px solid ${WALLPAPER_COLORS.border}`
                  : `1px solid ${theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.1)'
                    : 'rgba(0,0,0,0.05)'}`,
                transition: 'all 0.2s ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.palette.actualMode === 'wallpaper'
                    ? 'none'
                    : (theme.palette.mode === 'dark'
                      ? '0 4px 20px rgba(255,255,255,0.05)'
                      : '0 4px 20px rgba(0,0,0,0.05)'),
                }
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  mb: 0.5,
                  fontSize: '0.85rem',
                  color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.textSecondary : 'text.secondary'
                }}
              >
                Duration
              </Typography>
              <Typography 
                variant="h5" 
                fontWeight={600}
                sx={{
                  background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {formatWorkoutTime(workoutStats.duration)}
              </Typography>
            </Box>
      
            <Box
              sx={{
                borderRadius: '16px',
                p: 2,
                background: theme.palette.actualMode === 'wallpaper'
                  ? WALLPAPER_COLORS.bgLight
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.05)'
                    : 'rgba(0,0,0,0.02)'),
                border: theme.palette.actualMode === 'wallpaper'
                  ? `1px solid ${WALLPAPER_COLORS.border}`
                  : `1px solid ${theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.1)'
                    : 'rgba(0,0,0,0.05)'}`,
                transition: 'all 0.2s ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.palette.actualMode === 'wallpaper'
                    ? 'none'
                    : (theme.palette.mode === 'dark'
                      ? '0 4px 20px rgba(255,255,255,0.05)'
                      : '0 4px 20px rgba(0,0,0,0.05)'),
                }
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  mb: 0.5,
                  fontSize: '0.85rem',
                  color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.textSecondary : 'text.secondary'
                }}
              >
                Calories
              </Typography>
              <Typography 
                variant="h5" 
                fontWeight={600}
                sx={{
                  background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {workoutStats.caloriesBurned}
              </Typography>
            </Box>
      
            <Box
              sx={{
                borderRadius: '16px',
                p: 2,
                background: theme.palette.actualMode === 'wallpaper'
                  ? WALLPAPER_COLORS.bgLight
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.05)'
                    : 'rgba(0,0,0,0.02)'),
                border: theme.palette.actualMode === 'wallpaper'
                  ? `1px solid ${WALLPAPER_COLORS.border}`
                  : `1px solid ${theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.1)'
                    : 'rgba(0,0,0,0.05)'}`,
                transition: 'all 0.2s ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.palette.actualMode === 'wallpaper'
                    ? 'none'
                    : (theme.palette.mode === 'dark'
                      ? '0 4px 20px rgba(255,255,255,0.05)'
                      : '0 4px 20px rgba(0,0,0,0.05)'),
                }
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  mb: 0.5,
                  fontSize: '0.85rem',
                  color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.textSecondary : 'text.secondary'
                }}
              >
                Volume
              </Typography>
              <Typography 
                variant="h5" 
                fontWeight={600}
                sx={{
                  background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {workoutStats.totalVolume} {weightUnit}
              </Typography>
            </Box>
          </Box>
      
          <Divider 
            sx={{ 
              mb: 3,
              opacity: 0.6
            }} 
          />
      
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{
              mb: 1.5,
              fontSize: '1.1rem',
              color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit'
            }}
          >
            Muscles Worked
          </Typography>
      
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {workoutStats.musclesWorked.length > 0 ? (
              workoutStats.musclesWorked.map((muscle, index) => (
                <Chip
                  key={index}
                  label={muscle}
                  sx={{
                    borderRadius: '10px',
                    backgroundColor: theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(255,107,0,0.25)'
                      : (theme.palette.mode === 'dark'
                        ? 'rgba(255,107,0,0.15)'
                        : 'rgba(255,107,0,0.1)'),
                    color: '#FF6B00',
                    fontWeight: 500,
                    border: '1px solid rgba(255,107,0,0.2)',
                    py: 0.5,
                    '&:hover': {
                      backgroundColor: theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(255,107,0,0.3)'
                        : (theme.palette.mode === 'dark'
                          ? 'rgba(255,107,0,0.2)'
                          : 'rgba(255,107,0,0.15)'),
                    }
                  }}
                />
              ))
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.textSecondary : 'text.disabled'
                }}
              >
                No muscles tracked
              </Typography>
            )}
          </Box>
      
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{
              mb: 1.5,
              fontSize: '1.1rem',
              color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit'
            }}
          >
            Exercises Completed
          </Typography>
      
          <Box
            sx={{
              mb: 3,
              borderRadius: '12px',
              background: theme.palette.actualMode === 'wallpaper'
                ? WALLPAPER_COLORS.bgLight
                : (theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.03)'
                  : 'rgba(0,0,0,0.01)'),
              border: theme.palette.actualMode === 'wallpaper'
                ? `1px solid ${WALLPAPER_COLORS.border}`
                : `1px solid ${theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.08)'
                  : 'rgba(0,0,0,0.03)'}`,
            }}
          >
            {exercises.filter(ex => {
              const exerciseSets = completedSets[ex.id] || [];
              return exerciseSets.some(Boolean);
            }).map((exercise, index, filteredExercises) => {
              const exerciseSets = completedSets[exercise.id] || [];
              const completedSetCount = exerciseSets.filter(Boolean).length;
      
              return (
                <Box 
                  key={index}
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    py: 1.5,
                    px: 2,
                    borderBottom: index < filteredExercises.length - 1 
                      ? `1px solid ${theme.palette.divider}` 
                      : 'none'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box 
                      sx={{ 
                        width: 24, 
                        height: 24, 
                        borderRadius: '50%', 
                        background: 'rgba(255,107,0,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5
                      }}
                    >
                      <Check size={14} color="#FF6B00" />
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{
                        color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit'
                      }}
                    >
                      {exercise.name}
                    </Typography>
                  </Box>
                  <Typography 
                    variant="body2"
                    sx={{
                      color: '#FF6B00',
                      fontWeight: 500,
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255,107,0,0.1)'
                        : 'rgba(255,107,0,0.08)',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '8px',
                      fontSize: '0.8rem'
                    }}
                  >
                    {completedSetCount} sets
                  </Typography>
                </Box>
              );
            })}
      
            {exercises.filter(ex => {
              const exerciseSets = completedSets[ex.id] || [];
              return exerciseSets.some(Boolean);
            }).length === 0 && (
              <Typography
                variant="body2"
                sx={{
                  textAlign: 'center',
                  py: 3,
                  color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.textSecondary : 'text.disabled'
                }}
              >
                No exercises completed
              </Typography>
            )}
          </Box>
        </Box>
      
        {/* Footer with Button - Fixed */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          p: 3,
          borderTop: theme.palette.actualMode === 'wallpaper'
            ? `1px solid ${WALLPAPER_COLORS.border}`
            : `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.actualMode === 'wallpaper'
            ? WALLPAPER_COLORS.bgLight
            : (theme.palette.mode === 'dark'
              ? 'rgba(18, 18, 18, 0.98)'
              : 'rgba(255, 255, 255, 0.98)'),
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)'
        }}>
          <Button
            variant="contained"
            onClick={handleCloseWorkoutComplete}
            sx={{
              borderRadius: '14px',
              background: GRADIENT,
              color: '#FFFFFF',
              boxShadow: '0 4px 15px rgba(255,107,0,0.25)',
              px: 5,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              minWidth: '160px',
              '&:hover': {
                background: GRADIENT,
                boxShadow: '0 6px 20px rgba(255,107,0,0.3)',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            Done
          </Button>
        </Box>
      </Dialog>
      
      {/* Indicador de carga */}
      {loading && (
        <Box 
          sx={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </WorkoutContainer>
  );
};

export default StartEmptyWorkout;